import { graphql } from '@api/mesa'
import { RewardsGetPointsLedgerPageQuery } from '@api/mesa/graphql'
import { useLazyQuery } from '@apollo/client'
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'

export const REWARDS_GET_POINTS_LEDGER_PAGE = graphql(`
  query RewardsGetPointsLedgerPage($cursor: String, $limit: Int) {
    getPointsLedgerPage(cursor: $cursor, limit: $limit) {
      items {
        __typename
        id
        isPending
        points {
          value
          decimalPlaces
          isNegative
        }
        date
        canViewDetails
        ... on GoodwillPointsLedgerItem {
          notes
        }
        ... on CardPurchasePointsLedgerItem {
          merchantName
          pointsMultiplier
          transactionAmount {
            value
            decimalPlaces
          }
        }
        ... on CardReturnPointsLedgerItem {
          merchantName
          pointsMultiplier
          transactionAmount {
            value
            decimalPlaces
          }
        }
        ... on ExternalRedemptionPointsLedgerItem {
          orderDescription
        }
        ... on ExternalRedemptionRefundPointsLedgerItem {
          orderDescription
        }
        ... on StatementCreditRedemptionPointsLedgerItem {
          amount {
            value
            decimalPlaces
          }
        }
        ... on MortgageTransferPointsLedgerItem {
          status
        }
        ... on PromoCodeBonusPointsLedgerItem {
          promoCode
        }
        ... on LoyaltyTierEventPointsLedgerItem {
          reason
          eventDate
        }
      }
      nextCursor
      hasMore
    }
  }
`)

export type PointsLedgerItemQueryResult =
  RewardsGetPointsLedgerPageQuery['getPointsLedgerPage']['items'][number]

type PointsLedgerContextType = {
  ledger: PointsLedgerItemQueryResult[]
  refresh: () => Promise<void>
  fetchNextPage: () => Promise<void>
  isLoading: boolean
  hasMore: boolean
}

export const PointsLedgerContext = createContext<PointsLedgerContextType>({
  ledger: [],
  refresh: () => Promise.resolve(),
  fetchNextPage: () => Promise.resolve(),
  isLoading: false,
  hasMore: false,
})

export const PointsLedgerProvider = ({ children }: { children: React.ReactNode }) => {
  const [getPointsLedgerPage, { loading: isLoading }] = useLazyQuery(
    REWARDS_GET_POINTS_LEDGER_PAGE,
    {
      fetchPolicy: 'no-cache',
    },
  )
  const [nextCursor, setNextCursor] = useState<string | null>(null)
  const [ledger, setLedger] = useState<PointsLedgerItemQueryResult[]>([])
  const isFetchingNextPage = useRef(false)
  const fetchMorePending = useRef(false)
  const [hasMore, setHasMore] = useState(false)

  const loadPage = useCallback(
    async (cursor: string | null) => {
      const { data } = await getPointsLedgerPage({
        variables: { cursor, limit: 15 },
      })

      if (data?.getPointsLedgerPage) {
        setNextCursor(data.getPointsLedgerPage.nextCursor)
        setHasMore(data.getPointsLedgerPage.hasMore)

        // If the cursor is null, this must be a full refresh. Therefore, what's in the ledger
        // is stale and should be replaced; otherwise, just append the new page.
        if (cursor === null) {
          setLedger(data.getPointsLedgerPage.items)
        } else {
          setLedger((prev) => [...prev, ...data.getPointsLedgerPage.items])
        }
      } else {
        setHasMore(false)
      }
    },
    [getPointsLedgerPage],
  )

  const refresh = useCallback(async () => {
    // Don't let `refresh` and `fetchNextPage` run concurrently.
    isFetchingNextPage.current = true
    setNextCursor(null)
    setHasMore(true)

    try {
      await loadPage(null)
    } finally {
      isFetchingNextPage.current = false
    }
  }, [loadPage])

  const fetchNextPage = useCallback(async () => {
    // Don't let `fetchNextPage` run concurrently.
    if (isFetchingNextPage.current) {
      // Instead, signal that another page was requested.
      fetchMorePending.current = true
      return
    }

    try {
      isFetchingNextPage.current = true
      await loadPage(nextCursor)
    } finally {
      isFetchingNextPage.current = false
    }
  }, [nextCursor, loadPage])

  useEffect(() => {
    if (fetchMorePending.current) {
      fetchMorePending.current = false
      void fetchNextPage()
    }
  }, [fetchNextPage, ledger])

  const value = useMemo(
    () => ({ ledger, fetchNextPage, isLoading, hasMore, refresh }),
    [ledger, fetchNextPage, isLoading, hasMore, refresh],
  )

  return <PointsLedgerContext.Provider value={value}>{children}</PointsLedgerContext.Provider>
}

export const usePointsLedger = () => {
  const { ledger, fetchNextPage, isLoading, hasMore, refresh } = useContext(PointsLedgerContext)

  return {
    ledger,
    fetchNextPage,
    isLoading,
    hasMore,
    refresh,
    query: REWARDS_GET_POINTS_LEDGER_PAGE,
  }
}
